/**
 * Capability interface for dashboard widget components that support on-demand refresh.
 *
 * Design decision D2: capability detected by interface at runtime against the live
 * component instance — NOT by a declarative flag on `AuroraWidgetDef`.
 * A declared flag drifts from reality when the component changes; an interface check
 * cannot lie. Detection uses `isRefreshable(componentRef.instance)`.
 */
export interface RefreshableWidget {
  refresh(): void;
}

/**
 * Type guard: returns `true` when `value` satisfies the `RefreshableWidget` interface.
 *
 * Used by the tile chrome to decide whether to show the refresh button.
 * Checks the live component instance — never the declaration object.
 *
 * @example
 * ```typescript
 * if (isRefreshable(componentRef.instance)) {
 *   componentRef.instance.refresh();
 * }
 * ```
 */
export function isRefreshable(value: unknown): value is RefreshableWidget {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as RefreshableWidget).refresh === 'function'
  );
}
